import React from 'react';
import { HStack, IconButton, Box, Text, Button, VStack } from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { PaginationInfo as PaginationData } from '../../hooks/useVirtualizedGrid';

interface PaginationInfoProps {
  pagination: PaginationData;
  onPageChange: (page: number) => void;
  onNextPage: () => void;
  onPrevPage: () => void;
  showInfo?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

const PaginationInfo: React.FC<PaginationInfoProps> = ({
  pagination,
  onPageChange,
  onNextPage,
  onPrevPage,
  showInfo = true,
  size = 'md'
}) => {
  
  const { currentPage, totalPages, totalItems, startIndex, endIndex } = pagination;

  // Generar números de página para mostrar
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 5;
    
    if (totalPages <= maxVisible) {
      // Mostrar todas las páginas si son pocas
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Lógica para páginas con ellipsis
      if (currentPage <= 3) {
        pages.push(1, 2, 3, 4, '...', totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }
    }
    
    return pages;
  };

  if (totalPages <= 1) {
    return showInfo ? (
      <VStack spacing={2} mt={4}>
        <Text fontSize="sm" color="gray.600">
          {totalItems === 0 ? 'No hay elementos' : `${totalItems} elemento${totalItems !== 1 ? 's' : ''}`}
        </Text>
      </VStack>
    ) : null;
  }

  return (
    <VStack spacing={3} mt={4}>
      {/* Información de elementos */}
      {showInfo && (
        <Text fontSize="sm" color="gray.600">
          Mostrando {startIndex + 1} - {endIndex} de {totalItems} elementos
        </Text>
      )}
      
      {/* Controles de paginación */}
      <HStack spacing={2}>
        {/* Botón anterior */}
        <IconButton
          icon={<ChevronLeftIcon />}
          onClick={onPrevPage}
          isDisabled={currentPage === 1}
          aria-label="Página anterior"
          size={size}
          variant="outline"
          colorScheme="gray"
        />
        
        {/* Números de página */}
        <HStack spacing={1}>
          {getPageNumbers().map((page, index) => {
            if (page === '...') {
              return (
                <Text key={`ellipsis-${index}`} px={2} color="gray.500">
                  ...
                </Text>
              );
            }
            
            const pageNum = page as number;
            const isActive = pageNum === currentPage;
            
            return (
              <Button
                key={pageNum}
                size={size}
                variant={isActive ? "solid" : "outline"}
                colorScheme={isActive ? "red" : "gray"}
                bg={isActive ? "#5b182b" : "transparent"}
                color={isActive ? "white" : "gray.600"}
                borderColor={isActive ? "#5b182b" : "gray.300"}
                _hover={{
                  bg: isActive ? "#691f30" : "gray.50",
                  borderColor: isActive ? "#691f30" : "gray.400"
                }}
                onClick={() => onPageChange(pageNum)}
                minW="40px"
              >
                {pageNum}
              </Button>
            );
          })}
        </HStack>
        
        {/* Botón siguiente */}
        <IconButton
          icon={<ChevronRightIcon />}
          onClick={onNextPage}
          isDisabled={currentPage === totalPages}
          aria-label="Página siguiente"
          size={size}
          variant="outline"
          colorScheme="gray"
        />
      </HStack>
      
      {/* Información adicional de página */}
      <Text fontSize="xs" color="gray.500">
        Página {currentPage} de {totalPages}
      </Text>
    </VStack>
  );
};

export default PaginationInfo;